import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, Calendar, Users, CreditCard } from "lucide-react";
import api, { formatTRY, formatDate, resolveImageUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const STATUS_BADGE = {
    confirmed: { label: "Onaylandı", color: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300" },
    pending: { label: "Beklemede", color: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300" },
    cancelled: { label: "İptal Edildi", color: "bg-red-100 text-red-700" },
};

export default function MyBookingsPage() {
    const { user, loading: authLoading } = useAuth() || {};
    const navigate = useNavigate();
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (authLoading) return;
        if (!user) {
            navigate("/login", { state: { from: "/my-bookings" } });
            return;
        }
        api.get("/bookings/my")
            .then((r) => setBookings(r.data))
            .finally(() => setLoading(false));
    }, [user, authLoading, navigate]);

    return (
        <div data-testid="my-bookings-page" className="min-h-screen bg-slate-50 dark:bg-slate-950">
            <Navbar />
            <div className="container mx-auto px-4 md:px-6 max-w-5xl py-10">
                <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-2">Rezervasyonlarım</h1>
                <p className="text-slate-500 mb-8">{user?.full_name} · {bookings.length} rezervasyon</p>

                {loading ? (
                    <div className="space-y-4">{[1, 2, 3].map((i) => <div key={i} className="h-32 rounded-2xl bg-slate-200 dark:bg-slate-800 animate-pulse" />)}</div>
                ) : bookings.length === 0 ? (
                    <div className="text-center py-20 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                        <p className="text-slate-500 mb-4">Henüz bir rezervasyonunuz yok.</p>
                        <Link to="/search" className="inline-block px-6 py-3 rounded-xl bg-sky-500 text-white font-semibold">Otel Ara</Link>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {bookings.map((b) => {
                            const badge = STATUS_BADGE[b.status] || STATUS_BADGE.pending;
                            return (
                                <div key={b.id} data-testid={`booking-${b.id}`} className="flex flex-col md:flex-row gap-4 p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                                    <img src={resolveImageUrl(b.hotel_image)} alt={b.hotel_name} className="w-full md:w-44 h-32 object-cover rounded-xl" />
                                    <div className="flex-1">
                                        <div className="flex items-start justify-between gap-2">
                                            <div>
                                                <h3 className="font-display font-bold text-lg text-slate-900 dark:text-white">{b.hotel_name}</h3>
                                                <div className="flex items-center gap-1 text-sm text-slate-500"><MapPin className="h-3.5 w-3.5" /> {b.destination_name}</div>
                                            </div>
                                            <span className={`text-xs font-bold px-2 py-1 rounded-md ${badge.color}`}>{badge.label}</span>
                                        </div>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 text-xs">
                                            <Info icon={<Calendar className="h-3.5 w-3.5" />} label="Giriş" value={formatDate(b.check_in)} />
                                            <Info icon={<Calendar className="h-3.5 w-3.5" />} label="Çıkış" value={formatDate(b.check_out)} />
                                            <Info icon={<Users className="h-3.5 w-3.5" />} label="Misafir" value={`${b.adults}y, ${b.children}ç · ${b.rooms} oda`} />
                                            <Info icon={<CreditCard className="h-3.5 w-3.5" />} label="Tutar" value={formatTRY(b.total_amount)} highlight />
                                        </div>
                                        <div className="mt-3 text-xs text-slate-400">No: {b.id.slice(0, 8).toUpperCase()} · {b.room_type_name}</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            <Footer />
        </div>
    );
}

function Info({ icon, label, value, highlight }) {
    return (
        <div>
            <div className="flex items-center gap-1 text-slate-500 mb-0.5">{icon}{label}</div>
            <div className={`font-semibold ${highlight ? "text-sky-600 dark:text-sky-400" : "text-slate-900 dark:text-white"}`}>{value}</div>
        </div>
    );
}
